import { useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { errorMessage } from './format';

const useStyles = makeStyles({
  content: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
});

/**
 * Confirmation for irreversible or consequential actions (spec 15.3): says
 * what will happen, optionally asks for a comment or a typed value.
 */
export function ConfirmDialog({
  trigger,
  title,
  children,
  confirmLabel,
  input,
  onConfirm,
}: {
  trigger: JSX.Element;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  input?: { kind: 'comment' | 'typed'; label: string; required?: boolean; mustEqual?: string };
  onConfirm: (value: string) => Promise<unknown>;
}) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid =
    !input ||
    (input.mustEqual
      ? value.trim().toUpperCase() === input.mustEqual.toUpperCase()
      : !input.required || value.trim().length > 0);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(value);
      setOpen(false);
      setValue('');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
      <DialogTrigger disableButtonEnhancement>{trigger}</DialogTrigger>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent className={styles.content}>
            {children}
            {input?.kind === 'comment' && (
              <Field label={input.label} required={input.required}>
                <Textarea value={value} onChange={(_, d) => setValue(d.value)} resize="vertical" />
              </Field>
            )}
            {input?.kind === 'typed' && (
              <Field label={input.label} required>
                <Input value={value} onChange={(_, d) => setValue(d.value.toUpperCase())} autoFocus />
              </Field>
            )}
            {error && (
              <MessageBar intent="error" layout="multiline">
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
            <Button appearance="primary" disabled={!valid || busy} onClick={() => void confirm()}>
              {busy ? 'Working…' : confirmLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
